import type {
	ChecklistTemplateCreateBody,
	ChecklistTemplateDTO,
	ChecklistTemplateItemCreateBody,
	ChecklistTemplateItemDTO,
	ChecklistTemplateItemUpdateBody,
	ChecklistTemplateUpdateBody,
	TransitionHookCreateBody,
	TransitionHookDTO,
	TransitionHookUpdateBody,
	WorkflowActivateBody,
	WorkflowCreateBody,
	WorkflowDiagnosticsPage,
	WorkflowDTO,
	WorkflowRepairBody,
	WorkflowRepairReport,
	WorkflowReplacementBody,
	WorkflowResolverRuleCreateBody,
	WorkflowResolverRuleDTO,
	WorkflowResolverRuleUpdateBody,
	WorkflowStatusCreateBody,
	WorkflowStatusDTO,
	WorkflowStatusUpdateBody,
	WorkflowTransitionCreateBody,
	WorkflowTransitionDTO,
	WorkflowTransitionUpdateBody,
	WorkflowUpdateBody,
	WorkspaceAssignmentCreateBody,
	WorkspaceAssignmentDTO,
	WorkspaceAssignmentUpdateBody,
} from '@/domain/workflow-admin.types'
import { apiClient } from './api.client'

/**
 * Workflow administration API client — the ADMIN-only `work-order-service`
 * routes. Every administrative response is the standard `{ data }` envelope;
 * diagnostics adds the pagination block alongside it.
 */

/** Unwrap the `{ data }` envelope, tolerating a 204 with no body on list routes. */
const listOf = <T>(response: { status: number; data?: { data?: T[] } }): T[] =>
	response.status === 204 ? [] : (response.data?.data ?? [])

export const workflowAdminApi = {
	// ─── Workflows ────────────────────────────────────────────────────────────

	listWorkflows: (includeDeleted = false): Promise<WorkflowDTO[]> =>
		apiClient
			.get<{ data: WorkflowDTO[] }>('/workflows', includeDeleted ? { params: { includeDeleted: 'true' } } : undefined)
			.then(listOf),

	getWorkflow: (id: string): Promise<WorkflowDTO> =>
		apiClient.get<{ data: WorkflowDTO }>(`/workflows/${id}`).then((r) => r.data.data),

	createWorkflow: (body: WorkflowCreateBody): Promise<WorkflowDTO> =>
		apiClient.post<{ data: WorkflowDTO }>('/workflows', body).then((r) => r.data.data),

	updateWorkflow: (id: string, body: WorkflowUpdateBody): Promise<WorkflowDTO> =>
		apiClient.patch<{ data: WorkflowDTO }>(`/workflows/${id}`, body).then((r) => r.data.data),

	activateWorkflow: (id: string, body: WorkflowActivateBody): Promise<WorkflowDTO> =>
		apiClient.post<{ data: WorkflowDTO }>(`/workflows/${id}/activation`, body).then((r) => r.data.data),

	deactivateWorkflow: (id: string, body: WorkflowReplacementBody = {}): Promise<WorkflowDTO> =>
		apiClient.post<{ data: WorkflowDTO }>(`/workflows/${id}/deactivation`, body).then((r) => r.data.data),

	setDefaultWorkflow: (id: string): Promise<WorkflowDTO> =>
		apiClient.post<{ data: WorkflowDTO }>(`/workflows/${id}/default`).then((r) => r.data.data),

	// A soft-delete carries a body, so it uses axios's `{ data }` request option.
	deleteWorkflow: (id: string, body: WorkflowReplacementBody = {}): Promise<WorkflowDTO> =>
		apiClient.delete<{ data: WorkflowDTO }>(`/workflows/${id}`, { data: body }).then((r) => r.data.data),

	// ─── Statuses ─────────────────────────────────────────────────────────────

	listStatuses: (workflowId: string): Promise<WorkflowStatusDTO[]> =>
		apiClient.get<{ data: WorkflowStatusDTO[] }>(`/workflows/${workflowId}/statuses`).then(listOf),

	createStatus: (body: WorkflowStatusCreateBody): Promise<WorkflowStatusDTO> =>
		apiClient.post<{ data: WorkflowStatusDTO }>('/workflow-statuses', body).then((r) => r.data.data),

	/**
	 * Promoting a status to initial is this same call with `isInitial: true` —
	 * the backend demotes the previous initial status in the same transaction,
	 * so there is no separate replacement request to make.
	 */
	updateStatus: (statusId: string, body: WorkflowStatusUpdateBody): Promise<WorkflowStatusDTO> =>
		apiClient.patch<{ data: WorkflowStatusDTO }>(`/workflow-statuses/${statusId}`, body).then((r) => r.data.data),

	/**
	 * The workflow's complete ordered status id list; the backend renumbers
	 * positions densely from it and returns the authoritative order. A status's
	 * position is not patchable through `updateStatus` - this is the only call
	 * that moves one.
	 */
	reorderStatuses: (workflowId: string, statusIds: string[]): Promise<WorkflowStatusDTO[]> =>
		apiClient
			.post<{ data: WorkflowStatusDTO[] }>('/workflow-statuses/reorder', { workflowId, statusIds })
			.then((r) => r.data.data ?? []),

	deleteStatus: (statusId: string): Promise<WorkflowStatusDTO> =>
		apiClient.delete<{ data: WorkflowStatusDTO }>(`/workflow-statuses/${statusId}`).then((r) => r.data.data),

	// ─── Transitions ──────────────────────────────────────────────────────────

	listTransitions: (workflowId: string): Promise<WorkflowTransitionDTO[]> =>
		apiClient.get<{ data: WorkflowTransitionDTO[] }>(`/workflows/${workflowId}/transitions`).then(listOf),

	createTransition: (body: WorkflowTransitionCreateBody): Promise<WorkflowTransitionDTO> =>
		apiClient.post<{ data: WorkflowTransitionDTO }>('/workflow-transitions', body).then((r) => r.data.data),

	updateTransition: (transitionId: string, body: WorkflowTransitionUpdateBody): Promise<WorkflowTransitionDTO> =>
		apiClient
			.patch<{ data: WorkflowTransitionDTO }>(`/workflow-transitions/${transitionId}`, body)
			.then((r) => r.data.data),

	deleteTransition: (transitionId: string): Promise<WorkflowTransitionDTO> =>
		apiClient.delete<{ data: WorkflowTransitionDTO }>(`/workflow-transitions/${transitionId}`).then((r) => r.data.data),

	// ─── Resolver rules ───────────────────────────────────────────────────────

	listResolverRules: (): Promise<WorkflowResolverRuleDTO[]> =>
		apiClient.get<{ data: WorkflowResolverRuleDTO[] }>('/workflow-resolver-rules').then(listOf),

	createResolverRule: (body: WorkflowResolverRuleCreateBody): Promise<WorkflowResolverRuleDTO> =>
		apiClient.post<{ data: WorkflowResolverRuleDTO }>('/workflow-resolver-rules', body).then((r) => r.data.data),

	updateResolverRule: (ruleId: string, body: WorkflowResolverRuleUpdateBody): Promise<WorkflowResolverRuleDTO> =>
		apiClient
			.patch<{ data: WorkflowResolverRuleDTO }>(`/workflow-resolver-rules/${ruleId}`, body)
			.then((r) => r.data.data),

	deleteResolverRule: (ruleId: string): Promise<WorkflowResolverRuleDTO> =>
		apiClient.delete<{ data: WorkflowResolverRuleDTO }>(`/workflow-resolver-rules/${ruleId}`).then((r) => r.data.data),

	/** The full ordered id list; the backend renumbers priorities densely from it. */
	reorderResolverRules: (ruleIds: string[]): Promise<WorkflowResolverRuleDTO[]> =>
		apiClient
			.post<{ data: WorkflowResolverRuleDTO[] }>('/workflow-resolver-rules/reorder', { ruleIds })
			.then((r) => r.data.data ?? []),

	// ─── Diagnostics and repair ───────────────────────────────────────────────

	listDiagnostics: (page = 1, limit = 20): Promise<WorkflowDiagnosticsPage> =>
		apiClient.get<WorkflowDiagnosticsPage>('/workflow-diagnostics', { params: { page, limit } }).then((r) => r.data),

	/**
	 * One endpoint for both modes. `dryRun: true` previews and writes nothing;
	 * applying is a second call, never a local replay of the preview.
	 */
	repair: (body: WorkflowRepairBody): Promise<WorkflowRepairReport> =>
		apiClient.post<{ data: WorkflowRepairReport }>('/workflow-repairs', body).then((r) => r.data.data),

	// ─── Transition hooks ─────────────────────────────────────────────────────

	listTransitionHooks: (transitionId: string): Promise<TransitionHookDTO[]> =>
		apiClient.get<{ data: TransitionHookDTO[] }>(`/workflow-transitions/${transitionId}/hooks`).then(listOf),

	createTransitionHook: (body: TransitionHookCreateBody): Promise<TransitionHookDTO> =>
		apiClient.post<{ data: TransitionHookDTO }>('/transition-hooks', body).then((r) => r.data.data),

	updateTransitionHook: (hookId: string, body: TransitionHookUpdateBody): Promise<TransitionHookDTO> =>
		apiClient.patch<{ data: TransitionHookDTO }>(`/transition-hooks/${hookId}`, body).then((r) => r.data.data),

	deleteTransitionHook: (hookId: string): Promise<TransitionHookDTO> =>
		apiClient.delete<{ data: TransitionHookDTO }>(`/transition-hooks/${hookId}`).then((r) => r.data.data),

	// ─── Checklist templates ──────────────────────────────────────────────────

	listChecklistTemplates: (): Promise<ChecklistTemplateDTO[]> =>
		apiClient.get<{ data: ChecklistTemplateDTO[] }>('/checklist-templates').then(listOf),

	getChecklistTemplate: (templateId: string): Promise<ChecklistTemplateDTO> =>
		apiClient.get<{ data: ChecklistTemplateDTO }>(`/checklist-templates/${templateId}`).then((r) => r.data.data),

	createChecklistTemplate: (body: ChecklistTemplateCreateBody): Promise<ChecklistTemplateDTO> =>
		apiClient.post<{ data: ChecklistTemplateDTO }>('/checklist-templates', body).then((r) => r.data.data),

	updateChecklistTemplate: (templateId: string, body: ChecklistTemplateUpdateBody): Promise<ChecklistTemplateDTO> =>
		apiClient
			.patch<{ data: ChecklistTemplateDTO }>(`/checklist-templates/${templateId}`, body)
			.then((r) => r.data.data),

	deleteChecklistTemplate: (templateId: string): Promise<ChecklistTemplateDTO> =>
		apiClient.delete<{ data: ChecklistTemplateDTO }>(`/checklist-templates/${templateId}`).then((r) => r.data.data),

	createChecklistTemplateItem: (body: ChecklistTemplateItemCreateBody): Promise<ChecklistTemplateItemDTO> =>
		apiClient.post<{ data: ChecklistTemplateItemDTO }>('/checklist-template-items', body).then((r) => r.data.data),

	updateChecklistTemplateItem: (
		templateId: string,
		itemId: string,
		body: ChecklistTemplateItemUpdateBody,
	): Promise<ChecklistTemplateItemDTO> =>
		apiClient
			.patch<{ data: ChecklistTemplateItemDTO }>(`/checklist-templates/${templateId}/items/${itemId}`, body)
			.then((r) => r.data.data),

	deleteChecklistTemplateItem: (templateId: string, itemId: string): Promise<ChecklistTemplateItemDTO> =>
		apiClient
			.delete<{ data: ChecklistTemplateItemDTO }>(`/checklist-templates/${templateId}/items/${itemId}`)
			.then((r) => r.data.data),

	// ─── Workspace assignments ─────────────────────────────────────────────────

	listWorkspaceAssignments: (statusId: string): Promise<WorkspaceAssignmentDTO[]> =>
		apiClient
			.get<{ data: WorkspaceAssignmentDTO[] }>(`/workflow-statuses/${statusId}/workspace-assignments`)
			.then(listOf),

	createWorkspaceAssignment: (body: WorkspaceAssignmentCreateBody): Promise<WorkspaceAssignmentDTO> =>
		apiClient.post<{ data: WorkspaceAssignmentDTO }>('/workspace-assignments', body).then((r) => r.data.data),

	updateWorkspaceAssignment: (
		assignmentId: string,
		body: WorkspaceAssignmentUpdateBody,
	): Promise<WorkspaceAssignmentDTO> =>
		apiClient
			.patch<{ data: WorkspaceAssignmentDTO }>(`/workspace-assignments/${assignmentId}`, body)
			.then((r) => r.data.data),

	deleteWorkspaceAssignment: (assignmentId: string): Promise<WorkspaceAssignmentDTO> =>
		apiClient
			.delete<{ data: WorkspaceAssignmentDTO }>(`/workspace-assignments/${assignmentId}`)
			.then((r) => r.data.data),
}
